import { useState, useEffect, useRef, useCallback } from 'react';
import { JobData } from '../api/JobsService';
import { FormBuilderService } from '../api/FormBuilderService';
import { toast } from 'react-toastify';

// Type for questionnaire data with questions
export interface JobQuestionnaireData {
  questionnaireId: string;
  questionnaireName?: string;
  questions?: Array<{
    questionId: string;
    questionText: string;
    required?: boolean;
    type?: string;
  }>;
}

export interface CloneAttachContext {
  questionnaireId: string;
  questionnaireName: string;
}

// VI-579 follow-up: change shape lets callers nudge a count by `delta` (optimistic ±1 from
// attach/detach) or overwrite it with the `absolute` value (modal teaches the table the truth
// after a fresh server fetch). Either field may be omitted; only one is applied at a time.
export type ActiveJobsCountChange = { delta?: number; absolute?: number };

export type ActiveJobsCountUpdater = (questionnaireId: string, change: ActiveJobsCountChange) => void;

export const useJobQuestionnaires = (
  apiJobs: JobData[] | null,
  selectedQuestionnaireId: string | null,
  setSelectedQuestionnaireId: (id: string | null) => void,
  cloneContext: CloneAttachContext | null = null,
  clearCloneContext?: () => void,
  onCloneAttachSuccess?: (questionnaireName: string) => void,
  onActiveJobsCountChange?: ActiveJobsCountUpdater,
) => {
  const [jobsWithQuestionnaires, setJobsWithQuestionnaires] = useState<Set<number>>(new Set());
  const [jobQuestionnaireMap, setJobQuestionnaireMap] = useState<Map<number, string>>(new Map());
  // NEW: Store full questionnaire data including questions for each job
  const [jobQuestionnaireDataMap, setJobQuestionnaireDataMap] = useState<Map<number, JobQuestionnaireData>>(new Map());
  const [associatingJobs, setAssociatingJobs] = useState<Set<number>>(new Set());
  const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false);
  const [isLoadingJobQuestionnaires, setIsLoadingJobQuestionnaires] = useState(false);
  const [loadingJobIds, setLoadingJobIds] = useState<Set<number>>(new Set()); // Track which jobs are being checked
  
  // Track the job IDs to prevent re-fetching when only the reference changes
  const prevJobIdsRef = useRef<string>('');
  const hasFetchedRef = useRef(false);

  // VI-579 follow-up optimization: tracks job IDs we've already processed — either via the
  // scan effect below (regardless of whether the job has a questionnaire or not) or via the
  // out-of-band addKnownJobQuestionnaires path (drawer preload). The scan filters apiJobs
  // through this set so re-runs (e.g. after mergeJobs adds new jobs) only fetch for
  // genuinely-new jobIds — no more "Loading jobs and questionnaire data..." flash on every
  // apiJobs growth.
  const knownJobIdsRef = useRef<Set<number>>(new Set());

  // VI-579 follow-up: pre-populate the map/set for jobs whose questionnaireId is already
  // known from an out-of-band source (drawer preload). Avoids the redundant fan-out the
  // useEffect scan would otherwise do after mergeJobs() adds the new jobs to apiJobs.
  const addKnownJobQuestionnaires = useCallback(
    (links: Array<{ jobId: number; questionnaireId: string }>) => {
      if (links.length === 0) return;
      links.forEach((l) => knownJobIdsRef.current.add(l.jobId));
      setJobQuestionnaireMap((prev) => {
        const merged = new Map(prev);
        links.forEach((l) => merged.set(l.jobId, l.questionnaireId));
        return merged;
      });
      setJobsWithQuestionnaires((prev) => {
        const merged = new Set(prev);
        links.forEach((l) => merged.add(l.jobId));
        return merged;
      });
    },
    []
  );

  // Check which jobs have questionnaires - CONCURRENT/PARALLEL fetching with batching
  useEffect(() => {
    const checkJobQuestionnaires = async () => {
      if (!apiJobs || apiJobs.length === 0) return;

      // Create a stable identifier for the current jobs list
      const currentJobIds = apiJobs.map(j => j.id).sort().join(',');

      // Only fetch if the job IDs actually changed
      if (prevJobIdsRef.current === currentJobIds && hasFetchedRef.current) {
        console.log('✅ Skipping questionnaire check - job list unchanged');
        return;
      }

      prevJobIdsRef.current = currentJobIds;
      hasFetchedRef.current = true;

      // VI-579 follow-up optimization: skip jobs whose questionnaireId is already known
      // (typically added via addKnownJobQuestionnaires from the drawer preload). Avoids
      // a redundant checkJobQuestionnaire round-trip per known job. We use a ref instead
      // of the React state because state may not have propagated yet when this effect runs.
      const apiJobsToScan = apiJobs.filter(j => !knownJobIdsRef.current.has(j.id));
      const skippedCount = apiJobs.length - apiJobsToScan.length;
      if (skippedCount > 0) {
        console.log(`⏭️  Skipping ${skippedCount} jobs with already-known questionnaireId`);
      }
      if (apiJobsToScan.length === 0) {
        return;
      }

      // Mark only the not-yet-known jobs as loading
      setLoadingJobIds(new Set(apiJobsToScan.map(j => j.id)));
      setIsLoadingJobQuestionnaires(true);
      console.log(`🔄 Checking questionnaires for ${apiJobsToScan.length} jobs (parallel/batched)...`);

      // Process jobs in batches CONCURRENTLY for better performance
      const BATCH_SIZE = 10; // Process 10 jobs at a time concurrently

      for (let i = 0; i < apiJobsToScan.length; i += BATCH_SIZE) {
        const batch = apiJobsToScan.slice(i, i + BATCH_SIZE);
        const batchStartTime = Date.now();

        // Create promises for all jobs in the current batch
        const batchPromises = batch.map(async (job) => {
          try {
            const result = await FormBuilderService.checkJobQuestionnaire(job.id);
            return {
              jobId: job.id,
              hasQuestionnaire: result.hasQuestionnaire,
              questionnaireId: result.data?.questionnaireId,
              questionnaireName: result.data?.questionnaireName,
              questions: result.data?.questions, // Store full questions data
              error: null
            };
          } catch (error) {
            console.error(`Error checking questionnaire for job ${job.id}:`, error);
            return {
              jobId: job.id,
              hasQuestionnaire: false,
              questionnaireId: null,
              questionnaireName: null,
              questions: null,
              error
            };
          }
        });

        // Wait for all requests in this batch to complete CONCURRENTLY
        const batchResults = await Promise.all(batchPromises);

        // VI-579 follow-up: merge each batch's results into the existing state instead of
        // replacing it wholesale. Preserves entries that came from addKnownJobQuestionnaires
        // (drawer preload) or from optimistic ±1 callbacks on attach/detach. For each scanned
        // job, set OR remove its entry based on the scan result — so detached-elsewhere jobs
        // still get cleaned up correctly when scanned.
        const batchJobIds = new Set<number>();
        batchResults.forEach((r) => {
          batchJobIds.add(r.jobId);
          // VI-579 follow-up optimization: mark every processed job as "known" so future
          // apiJobs changes don't trigger a redundant re-check for it. Applies whether the
          // job has a questionnaire or not — we definitively scanned it once.
          knownJobIdsRef.current.add(r.jobId);
        });

        setJobsWithQuestionnaires((prev) => {
          const merged = new Set(prev);
          batchResults.forEach((r) => {
            if (r.hasQuestionnaire) merged.add(r.jobId);
            else merged.delete(r.jobId);
          });
          return merged;
        });
        setJobQuestionnaireMap((prev) => {
          const merged = new Map(prev);
          batchResults.forEach((r) => {
            if (r.hasQuestionnaire && r.questionnaireId) {
              merged.set(r.jobId, r.questionnaireId);
            } else {
              merged.delete(r.jobId);
            }
          });
          return merged;
        });
        setJobQuestionnaireDataMap((prev) => {
          const merged = new Map(prev);
          batchResults.forEach((r) => {
            if (r.hasQuestionnaire && r.questionnaireId) {
              merged.set(r.jobId, {
                questionnaireId: r.questionnaireId,
                questionnaireName: r.questionnaireName ?? undefined,
                questions: r.questions ?? undefined,
              });
            } else {
              merged.delete(r.jobId);
            }
          });
          return merged;
        });

        // Remove batch jobs from loading set
        setLoadingJobIds(prev => {
          const updated = new Set(prev);
          batchJobIds.forEach(id => updated.delete(id));
          return updated;
        });

        const batchDuration = Date.now() - batchStartTime;
        console.log(`📦 Batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(apiJobsToScan.length / BATCH_SIZE)} completed in ${batchDuration}ms (${batch.length} jobs)`);
      }

      setIsLoadingJobQuestionnaires(false);
      console.log(`✅ Finished checking ${apiJobsToScan.length} questionnaires (parallel)`);
    };

    checkJobQuestionnaires();
  }, [apiJobs]);

  const handleOpenOffCanvas = (questionnaireId: string) => {
    // console.log('Opening job selection off-canvas with Questionnaire ID:', questionnaireId);
    setSelectedQuestionnaireId(questionnaireId);
    setIsOffCanvasOpen(true);
  };

  const handleCloseOffCanvas = () => {
    // console.log('Closing job selection off-canvas, Questionnaire ID was:', selectedQuestionnaireId);
    setIsOffCanvasOpen(false);
  };
  
  // Handler for the Select/Detach button click
  const handleActionClick = async (jobId?: number) => {
    if (!jobId || !selectedQuestionnaireId) return;
    
    // Add this job to the set of jobs being processed - use functional update pattern
    setAssociatingJobs(prevJobs => {
      const newJobs = new Set(prevJobs);
      newJobs.add(jobId);
      return newJobs;
    });
    
    // Safety timeout to ensure button doesn't get stuck in processing state
    const safetyTimeout = setTimeout(() => {
      setAssociatingJobs(prevJobs => {
        if (prevJobs.has(jobId)) {
          const newJobs = new Set(prevJobs);
          newJobs.delete(jobId);
          console.log(`Safety timeout cleared processing state for job ${jobId}`);
          return newJobs;
        }
        return prevJobs;
      });
    }, 10000); // 10 seconds timeout
    
    try {
      // Check if the job already has a questionnaire
      const hasQuestionnaire = jobsWithQuestionnaires.has(jobId);
      
      if (hasQuestionnaire) {
        try {
          // console.log('Detaching questionnaire from job...');
          // Get the currently associated questionnaire ID from the map if available
          const currentQuestionnaireId = jobQuestionnaireMap.get(jobId);
          
          if (currentQuestionnaireId) {
            // console.log(`Using stored questionnaire ID for detaching: ${currentQuestionnaireId}`);
          } else {
            // console.log(`No stored questionnaire ID found for job ${jobId}, using selected ID: ${selectedQuestionnaireId}`);
          }
          
          // Detach the questionnaire - use the mapped ID if available, otherwise use selected ID
          const detachedQuestionnaireId = currentQuestionnaireId || selectedQuestionnaireId;
          await FormBuilderService.detachQuestionnaireFromJob(jobId, detachedQuestionnaireId);
          
          // Update the state to remove this job from jobsWithQuestionnaires
          setJobsWithQuestionnaires(prevJobs => {
            const updated = new Set(prevJobs);
            updated.delete(jobId);
            return updated;
          });
          
          // Also update the map
          setJobQuestionnaireMap(prevMap => {
            const updated = new Map(prevMap);
            updated.delete(jobId);
            return updated;
          });

          // VI-579 follow-up (Bug 1): notify the questionnaires table to optimistically decrement
          // its cached "Jobs Attached" count for the questionnaire we just detached from.
          onActiveJobsCountChange?.(detachedQuestionnaireId, { delta: -1 });
          
          // Show success notification
          toast.success(`Questionnaire successfully detached from job ID: ${jobId}`);
        } catch (detachError: any) {
          console.error("Error detaching questionnaire:", detachError);
          // Log more detailed information about the error
          if (detachError.response) {
            console.error("Error response status:", detachError.response.status);
            console.error("Error response data:", detachError.response.data);
            console.error("Error response headers:", detachError.response.headers);
            
            // Specific handling for 415 errors
            if (detachError.response.status === 415) {
              console.error("Received 415 Unsupported Media Type - This is likely due to missing or incorrect Content-Type header");
            }
          }
          toast.error(`Failed to detach questionnaire. Please try again.`);
        }
      } else {
        // Associate a questionnaire with the job
        try {
          // console.log('Associating Questionnaire ID:', selectedQuestionnaireId, 'with Job ID:', jobId);
          // Call the service to associate the questionnaire with the job
          await FormBuilderService.associateQuestionnaireWithJob(jobId, selectedQuestionnaireId);
          
          // Update the state to add this job to jobsWithQuestionnaires
          setJobsWithQuestionnaires(prevJobs => {
            const updated = new Set(prevJobs);
            updated.add(jobId);
            return updated;
          });
          
          // Update the map with the new association
          setJobQuestionnaireMap(prevMap => {
            const updated = new Map(prevMap);
            updated.set(jobId, selectedQuestionnaireId);
            return updated;
          });

          // VI-579 follow-up (Bug 1): notify the questionnaires table to optimistically increment
          // its cached "Jobs Attached" count for the questionnaire we just attached to.
          onActiveJobsCountChange?.(selectedQuestionnaireId, { delta: +1 });
          
          // Show success notification — clone-specific toast when this attach
          // is the tail end of a Clone flow, otherwise the default association toast.
          // The clone-flow toast is rendered by the parent (Home) using the JT
          // custom toast UI to match the Figma design, not react-toastify.
          if (cloneContext && cloneContext.questionnaireId === selectedQuestionnaireId) {
            onCloneAttachSuccess?.(cloneContext.questionnaireName);
            clearCloneContext?.();
          } else {
            toast.success(`Questionnaire successfully associated with job ID: ${jobId}`);
          }
        } catch (associateError: any) {
          console.error("Error associating questionnaire:", associateError);
          // Log more detailed information about the error
          if (associateError.response) {
            console.error("Error response status:", associateError.response.status);
            console.error("Error response data:", associateError.response.data);
            console.error("Error response headers:", associateError.response.headers);
            
            // Specific handling for 415 errors
            if (associateError.response.status === 415) {
              console.error("Received 415 Unsupported Media Type - This is likely due to missing or incorrect Content-Type header");
            }
          }
          toast.error(`Failed to associate questionnaire. Please try again.`);
        }
      }
    } catch (error) {
      console.error("Error processing questionnaire action for job:", error);
      toast.error(`Failed to process questionnaire action. Please try again.`);
    } finally {
      // Clear the safety timeout
      clearTimeout(safetyTimeout);
      
      // Add a small delay to prevent immediate re-clicking
      setTimeout(() => {
        // Remove this job from the processing set when done - use functional update pattern
        setAssociatingJobs(prevJobs => {
          const newJobs = new Set(prevJobs);
          newJobs.delete(jobId);
          return newJobs;
        });
      }, 300); // 300ms delay for better UX
    }
  };
  
  // Helper function to determine the action button text
  const getActionButtonText = (jobId: number) => {
    if (associatingJobs.has(jobId)) {
      return "Processing...";
    }
    
    if (jobsWithQuestionnaires.has(jobId)) {
      return "Detach";
    }
    
    return "Select";
  };

  // Helper function to determine the action button variant
  const getActionButtonVariant = (jobId: number): 'outline-primary' | 'outline-danger' | 'transparent' => {
    if (associatingJobs.has(jobId)) {
      return 'transparent';
    }
    
    if (jobsWithQuestionnaires.has(jobId)) {
      return 'outline-danger';
    }
    
    return 'outline-primary';
  };

  const refreshSingleJobQuestionnaire = async (jobId: number) => {
    try {
      const result = await FormBuilderService.checkJobQuestionnaire(jobId);
      
      // Update the sets/maps with the new data
      setJobsWithQuestionnaires(prevJobs => {
        const newJobs = new Set(prevJobs);
        if (result.hasQuestionnaire) {
          newJobs.add(jobId);
        } else {
          newJobs.delete(jobId);
        }
        return newJobs;
      });
      
      setJobQuestionnaireMap(prevMap => {
        const newMap = new Map(prevMap);
        if (result.hasQuestionnaire && result.data?.questionnaireId) {
          newMap.set(jobId, result.data.questionnaireId);
        } else {
          newMap.delete(jobId);
        }
        return newMap;
      });
    } catch (error) {
      console.error(`Error refreshing questionnaire for job ${jobId}:`, error);
    }
  };

  return {
    jobsWithQuestionnaires,
    jobQuestionnaireMap,
    jobQuestionnaireDataMap, // NEW: Full questionnaire data including questions
    associatingJobs,
    isOffCanvasOpen,
    setIsOffCanvasOpen,
    handleOpenOffCanvas,
    handleCloseOffCanvas,
    handleActionClick,
    getActionButtonText,
    getActionButtonVariant,
    isLoadingJobQuestionnaires,
    loadingJobIds, // Export individual job loading states
    refreshSingleJobQuestionnaire,
    addKnownJobQuestionnaires, // VI-579 follow-up: lets drawer preload skip the redundant scan
  };
}; 